import {Box, Link, Stack, Typography, useTheme} from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import Masonry from '@mui/lab/Masonry';
import {useDispatch, useSelector} from 'react-redux';
import {selectAllProcesses} from 'app/store/operatingModel/operatingModelSlice';
import {setProcessPageTab, setSelectedProcess} from 'app/store/appState/appStateSlice';
import {selectUserType} from 'app/store/users/userSlice';
import {useNavigate} from 'react-router-dom';
import {Custom_Encode_URL, ProcessTabs, LogInType} from 'app/types/Shared';
import PopUp from 'components/PopUp/PopUp';
import {useState} from 'react';

interface ModalLink {
  label: string;
  url: string;
  openInNewWindow?: boolean;
}
interface ResourceLink {
  label: string;
  url: string;
  retired?: boolean;
  modal?: {
    takeda: {
      header: string;
      description: string;
      modalLinks: ModalLink[];
    };
    external: {
      header: string;
      description: string;
      modalLinks: ModalLink[];
    };
  };
}

interface ResourceGroup {
  category: string;
  links: ResourceLink[];
}

type LinksGridProps = {
  resourceGroups: ResourceGroup[];
};

const LinksGrid = ({resourceGroups}: LinksGridProps) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const ttdomProcesses = useSelector(selectAllProcesses)?.filter(
    (process) => process.operating_models_list === 'TTDOM' && process.parent_process !== null,
  );
  const userType = useSelector(selectUserType);
  const navigate = useNavigate();

  // Determine if user is internal or external
  const isInternallUser = userType === LogInType.Internal;

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState<ResourceLink | null>(null);

  const handleOpenModal = (link: ResourceLink) => {
    setSelectedLink(link);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedLink(null);
  };

  const navigateToProcessPage = (link: ResourceLink) => {
    const TTDOMProcess_id = '(TTDOM) ' + link.label;
    const process = ttdomProcesses?.find(
      (process) => process.process_id?.replaceAll(' ', '') === TTDOMProcess_id?.replaceAll(' ', ''),
    );
    if (process) {
      dispatch(setSelectedProcess(process));
      dispatch(setProcessPageTab(ProcessTabs.RACI));
      navigate('/process/' + Custom_Encode_URL(process.name) + '/' + process.operating_models_list);
    }
  };

  return (
    <Box px={4} sx={{background: theme.palette.grey[100], minHeight: '500px'}}>
      <Typography variant='h1' py={2}>
        Takeda Trial Delivery Operating Model (TTDOM) Processes
      </Typography>
      <Box py={'10px'}>
        <Masonry columns={{sm: 1, md: 2, lg: 3}} spacing={4}>
          {resourceGroups.map((group, index) => (
            <div key={index}>
              <Typography variant='h3' sx={{mb: 2}}>
                {group.category}
              </Typography>
              <Stack spacing={2}>
                {group.links.map((link, linkIndex) => {
                  // Determine if the link is retired and/or has a modal content and render accordingly
                  const isRetired = Boolean(link?.retired);
                  const hasModal = Boolean(link?.modal);

                  return isRetired ? (
                    hasModal ? (
                      <Typography
                        key={linkIndex}
                        variant='h6'
                        onClick={() => handleOpenModal(link)}
                        sx={{
                          display: 'inline-block',
                          position: 'relative',
                          cursor: 'pointer',
                          fontStyle: 'italic',
                          marginTop: '10px !important',
                          opacity: 0.6,
                          '&:hover .hover-icon, &:hover': {
                            opacity: 1,
                          },
                        }}
                      >
                        {link.label}
                        <Box
                          className='hover-icon'
                          sx={{
                            display: 'inline',
                            position: 'relative',
                            top: '5px',
                            left: '5px',
                            opacity: 0,
                            transition: 'opacity 0.2s ease-in-out',
                          }}
                        >
                          <MoreHorizIcon fontSize='small' sx={{color: '#A1A4AC'}} />
                        </Box>
                      </Typography>
                    ) : (
                      <Typography variant='h6' key={linkIndex}>
                        {link.label}
                      </Typography>
                    )
                  ) : (
                    <Link
                      variant='h6'
                      color={'primary.main'}
                      key={linkIndex}
                      underline='hover'
                      sx={{cursor: 'pointer'}}
                      onClick={() => navigateToProcessPage(link)}
                      target='_blank'
                    >
                      {link.label}
                    </Link>
                  );
                })}
              </Stack>
            </div>
          ))}
        </Masonry>
      </Box>

      {/* PopUp component for modal links */}
      {selectedLink &&
        (() => {
          // Get modal content based on user type
          const modalContent = isInternallUser ? selectedLink.modal?.takeda : selectedLink.modal?.external;
          const modalHeader = modalContent?.header || '';
          const modalDescription = modalContent?.description || '';
          const modalLinks = modalContent?.modalLinks || [];

          return (
            <PopUp
              title={modalHeader}
              open={modalOpen}
              onConfirm={handleCloseModal}
              confirmText='Close'
              maxWidth='sm'
              width='600px'
            >
              <Stack spacing={2}>
                {modalDescription && <Typography variant='body1'>{modalDescription}</Typography>}
                <ul>
                  {modalLinks.map((modalLink, idx) => (
                    <li key={idx} style={{margin: '25px 0'}}>
                      <Link
                        href={modalLink.url}
                        // Set openInNewWindow in modalLInks to true on TDOMLinks.json to open in new window
                        target={modalLink.openInNewWindow ? '_blank' : '_self'}
                        rel='noopener noreferrer'
                        sx={{display: 'block'}}
                      >
                        {modalLink.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </Stack>
            </PopUp>
          );
        })()}
    </Box>
  );
};

export default LinksGrid;
